import React, { useState } from "react";
import axios from "axios";
import { PageHeader } from "./About";
import { Phone, Mail, MapPin, Send, Clock, Calendar, Printer } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { companyInfo } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Missing info", description: "Please provide your name and email.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: "Message sent!", description: "We'll be in touch within 24 hours." });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call 1 (888) 96-KNIGHT.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <main>
      <PageHeader title="Contact Us" subtitle="GET IN TOUCH" />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Intro header — full width above the grid */}
          <div className="mb-12 text-center">
            <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">REACH OUT</p>
            <h2 className="font-display text-4xl font-bold text-knight-navy mb-4">Let's talk about your credit journey</h2>
            <p className="text-knight-navy/75 leading-relaxed md:whitespace-nowrap">Schedule your free consultation or send us a message. Our team typically responds within 24 business hours.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* LEFT: Zcal calendar */}
            <div className="card-intense bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden order-2 lg:order-1 group">
              <div className="px-6 py-5 border-b border-slate-200 group-hover:border-knight-gold/40 transition-colors">
                <p className="text-knight-gold font-semibold tracking-widest text-xs mb-1">BOOK A CALL</p>
                <h3 className="font-display text-2xl font-bold text-knight-navy group-hover:text-knight-navy-dark transition-colors">Pick a time on Damon&apos;s calendar</h3>
                <p className="text-sm text-knight-navy/65 mt-1">Free 30-minute consultation. No obligation.</p>
              </div>
              <iframe
                src="https://zcal.co/knightmediagroup/kcc1?embed=1"
                title="Book a consultation"
                className="w-full"
                style={{ height: 820, border: 0 }}
                loading="lazy"
              />
              <p className="text-center text-xs text-knight-navy/50 py-3">Trouble loading? <a href="https://zcal.co/knightmediagroup/kcc1" target="_blank" rel="noopener noreferrer" className="text-knight-navy font-semibold underline">Open scheduler in a new tab</a>.</p>
            </div>

            {/* RIGHT: Message form */}
            <div className="order-1 lg:order-2">
              <form onSubmit={submit} className="card-intense bg-white rounded-3xl border border-slate-200 p-8 shadow-lg group">
                <h3 className="font-display text-2xl font-bold text-knight-navy mb-6 group-hover:text-knight-navy-dark transition-colors">Send us a message</h3>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-semibold text-knight-navy/70 mb-1.5 block">Full Name *</label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-knight-navy/70 mb-1.5 block">Email *</label>
                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-knight-navy/70 mb-1.5 block">Phone</label>
                    <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-knight-navy/70 mb-1.5 block">Service Interest</label>
                    <Select value={form.service} onValueChange={v => setForm({ ...form, service: v })}>
                      <SelectTrigger><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="repair">Credit Repair</SelectItem>
                        <SelectItem value="education">Credit Education</SelectItem>
                        <SelectItem value="monitoring">Credit Monitoring</SelectItem>
                        <SelectItem value="plan">Credit Action Plan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="text-xs font-semibold text-knight-navy/70 mb-1.5 block">Message</label>
                  <Textarea
                    rows={7}
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us a bit about your credit goals..."
                  />
                </div>
                <Button type="submit" size="lg" disabled={submitting} className="bg-knight-navy hover:bg-knight-navy-dark text-white font-bold w-full sm:w-auto btn-lift disabled:opacity-50">
                  {submitting ? "Sending..." : <>Send Message <Send size={16} className="ml-2" /></>}
                </Button>

                {/* SMS / Email consent disclaimer */}
                <p className="mt-6 text-[11.5px] leading-relaxed text-knight-navy/55" data-testid="contact-sms-disclaimer">
                  <span className="font-semibold text-knight-navy/75">*Disclaimer:</span> By providing my contact information to Knight Credit Consultants, I acknowledge and give my explicit consent to be contacted via SMS and receive emails for various purposes, which may include marketing and promotional content. Message and data rates may apply. Message frequency may vary. Reply STOP to opt-out. Reply HELP for more information. Refer to our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-knight-navy">Privacy Policy</a> for more information. No mobile information will be shared with third parties/affiliates for marketing/promotional purposes.
                </p>
              </form>
            </div>
          </div>

          {/* Contact info cards UNDERNEATH both — 2 columns, navy gradient design */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <p className="text-knight-gold font-semibold tracking-widest text-xs mb-2">DIRECT LINES</p>
              <h3 className="font-display text-3xl font-bold text-knight-navy">Reach Damon &amp; the team</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                { icon: Phone, label: "Toll-Free", value: companyInfo.tollFree, sub: "Call us 24/7 \u2014 quickest response", href: `tel:${companyInfo.tollFree.replace(/[^\d+]/g, "")}` },
                { icon: Phone, label: "Office Line", value: companyInfo.phones[0], sub: "Local Cincinnati line", href: `tel:${companyInfo.phones[0].replace(/[^\d+]/g, "")}` },
                { icon: Printer, label: "Fax", value: companyInfo.fax, sub: "Send documents securely" },
                { icon: Mail, label: "Email", value: companyInfo.email, sub: "We reply within 24 hours", href: `mailto:${companyInfo.email}` },
                { icon: MapPin, label: "Location", value: companyInfo.address, sub: "Serving clients nationwide" },
                { icon: Clock, label: "Hours", value: "Mon \u2013 Fri  9am \u2013 6pm EST", sub: "Sat by appointment" }
              ].map((item, i) => {
                const inner = (
                  <>
                    {/* Square gold icon template */}
                    <div className="w-14 h-14 rounded-xl bg-knight-gold text-knight-navy flex items-center justify-center flex-shrink-0 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:-rotate-6 group-hover:shadow-knight-gold/40">
                      <item.icon size={22} strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10.5px] font-bold tracking-[0.22em] text-knight-gold mb-1">{item.label.toUpperCase()}</p>
                      <p className="font-bold text-white break-words text-lg leading-snug transition-transform duration-300 group-hover:translate-x-0.5">{item.value}</p>
                      <p className="text-xs text-white/65 mt-1">{item.sub}</p>
                    </div>
                    {/* Subtle moving sheen */}
                    <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/8 to-transparent group-hover:translate-x-full transition-transform duration-[1200ms]" />
                  </>
                );
                const cls = "card-intense card-intense-dark knight-gradient text-white relative flex items-start gap-5 p-6 rounded-2xl overflow-hidden border border-white/10 group cursor-pointer";
                return item.href ? (
                  <a key={i} href={item.href} className={cls}>{inner}</a>
                ) : (
                  <div key={i} className={cls}>{inner}</div>
                );
              })}
            </div>

            <div className="mt-8 text-center">
              <a href="https://zcal.co/knightmediagroup/kcc1" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-knight-gold hover:bg-knight-gold/90 text-knight-navy font-bold btn-lift px-8">
                  <Calendar size={18} className="mr-2" /> Schedule A Consultation
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
